import React from 'react';
import { View, Text, StyleSheet } from 'react-native';

// Zporti theme colors based on existing components
const THEME = {
  colors: {
    primary: '#2D3748',      // Dark background
    secondary: '#4A5568',    // Lighter dark
    accent: '#48BB78',       // Green accent
    success: '#38A169',      // Success green
    error: '#E53E3E',        // Error red
    warning: '#DD6B20',      // Warning orange
    info: '#3182CE',         // Info blue
    white: '#FFFFFF',
    gray: {
      100: '#F7FAFC',
      200: '#EDF2F7',
      300: '#E2E8F0',
      400: '#CBD5E0',
      500: '#A0AEC0',
      600: '#718096',
      700: '#4A5568',
      800: '#2D3748',
      900: '#1A202C'
    }
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24
  }
};

export interface ZportiBadgeProps {
  /** Badge text content */
  text?: string;
  /** Badge variant */
  variant?: 'success' | 'error' | 'warning' | 'info' | 'notification' | 'score' | 'status' | 'secondary';
  /** Badge size (tab = cramped tab bar) */
  size?: 'small' | 'medium' | 'large' | 'tab';
  /** Number to display (for notification badges) */
  count?: number;
  /** Maximum count to show before showing "+" */
  maxCount?: number;
  /** Whether to show as a dot instead of text */
  dot?: boolean;
  /** Custom background color */
  backgroundColor?: string;
  /** Custom text color */
  textColor?: string;
  /** Custom border color */
  borderColor?: string;
  /** Whether the badge is visible */
  visible?: boolean;
  /** Test ID for testing */
  testID?: string;
}

export const ZportiBadge: React.FC<ZportiBadgeProps> = ({
  text,
  variant = 'info',
  size = 'medium',
  count,
  maxCount = 99,
  dot = false,
  backgroundColor,
  textColor,
  borderColor,
  visible = true,
  testID,
}) => {
  if (!visible) return null;

  const getVariantColors = () => {
    switch (variant) {
      case 'success':
        return {
          background: backgroundColor || THEME.colors.success,
          text: textColor || THEME.colors.white,
          border: borderColor || THEME.colors.success
        };
      case 'error':
        return {
          background: backgroundColor || THEME.colors.error,
          text: textColor || THEME.colors.white,
          border: borderColor || THEME.colors.error
        };
      case 'warning':
        return {
          background: backgroundColor || THEME.colors.warning,
          text: textColor || THEME.colors.white,
          border: borderColor || THEME.colors.warning
        };
      case 'info':
        return {
          background: backgroundColor || THEME.colors.info,
          text: textColor || THEME.colors.white,
          border: borderColor || THEME.colors.info
        };
      case 'notification':
        return {
          background: backgroundColor || THEME.colors.error,
          text: textColor || THEME.colors.white,
          border: borderColor || THEME.colors.error
        };
      case 'score':
        return {
          background: backgroundColor || THEME.colors.primary,
          text: textColor || THEME.colors.white,
          border: borderColor || THEME.colors.primary
        };
      case 'status':
        return {
          background: backgroundColor || THEME.colors.gray[500],
          text: textColor || THEME.colors.white,
          border: borderColor || THEME.colors.gray[500]
        };
      default:
        return {
          background: backgroundColor || THEME.colors.info,
          text: textColor || THEME.colors.white,
          border: borderColor || THEME.colors.info
        };
    }
  };

  const colors = getVariantColors();

  const getSizeStyles = () => {
    switch (size) {
      case 'tab':
        return {
          container: {
            minWidth: 12,
            height: 14,
            paddingHorizontal: 3,
            borderRadius: 7,
          },
          text: {
            fontSize: 8,
            lineHeight: 12,
          },
          dot: {
            width: 6,
            height: 6,
            borderRadius: 3,
          }
        };
      case 'small':
        return {
          container: {
            minWidth: 16,
            height: 20,
            paddingHorizontal: 4,
            borderRadius: 8,
          },
          text: {
            fontSize: 10,
            lineHeight: 16,
          },
          dot: {
            width: 8,
            height: 8,
            borderRadius: 4,
          }
        };
      case 'large':
        return {
          container: {
            minWidth: 32,
            height: 32,
            paddingHorizontal: 12,
            borderRadius: 16,
          },
          text: {
            fontSize: 16,
            lineHeight: 32,
          },
          dot: {
            width: 12,
            height: 12,
            borderRadius: 6,
          }
        };
      default: // medium
        return {
          container: {
            minWidth: 24,
            height: 24,
            paddingHorizontal: 8,
            borderRadius: 12,
          },
          text: {
            fontSize: 12,
            lineHeight: 20,
          },
          dot: {
            width: 10,
            height: 10,
            borderRadius: 5,
          }
        };
    }
  };

  const sizeStyles = getSizeStyles();

  const getDisplayText = () => {
    if (dot) return '';
    if (count !== undefined) {
      return count > maxCount ? `${maxCount}+` : count.toString();
    }
    return text || '';
  };

  const displayText = getDisplayText();

  const badgeStyle = [
    styles.badge,
    sizeStyles.container,
    {
      backgroundColor: colors.background,
      borderColor: colors.border,
    },
    dot && sizeStyles.dot,
    dot && { paddingHorizontal: 0, minWidth: 0 }
  ];

  const textStyle = [
    styles.text,
    sizeStyles.text,
    {
      color: colors.text,
    }
  ];

  return (
    <View style={badgeStyle} testID={testID}>
      {!dot && (
        <Text
          style={textStyle}
          numberOfLines={1}
          accessibilityLabel={displayText}
          accessibilityRole="text"
        >
          {displayText}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  badge: {
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  text: {
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default ZportiBadge;
